import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import {
  formatPercentWithColor,
  getSignalColor,
  TYPOGRAPHY,
} from '../../theme/forecastStyles';
import type { StockHistoryReturnsResponse } from '../../lib/api/stock-history';

interface AverageReturnsTableProps {
  returns: StockHistoryReturnsResponse;
}

export default function AverageReturnsTable({ returns }: AverageReturnsTableProps) {
  return (
    <Card sx={{ bgcolor: 'background.default', height: '100%' }}>
      <CardContent sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 2, mb: 3 }}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary' }}>
              Average Returns by Signal
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Expected realized return for each forecast type across 1d, 3d, 7d horizons.
            </Typography>
          </Box>
        </Box>

        {returns.length > 0 ? (
          <Box sx={{ overflowX: 'auto' }}>
            <TableContainer>
              <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ color: 'text.secondary', fontSize: TYPOGRAPHY.tiny }}>Signal</TableCell>
                  <TableCell align="right" sx={{ color: 'text.secondary', fontSize: TYPOGRAPHY.tiny }}>1d</TableCell>
                  <TableCell align="right" sx={{ color: 'text.secondary', fontSize: TYPOGRAPHY.tiny }}>3d</TableCell>
                  <TableCell align="right" sx={{ color: 'text.secondary', fontSize: TYPOGRAPHY.tiny }}>7d</TableCell>
                  <TableCell align="right" sx={{ color: 'text.secondary', fontSize: TYPOGRAPHY.tiny }}>Samples</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {returns.map((item) => (
                  <TableRow key={item.signal}>
                    <TableCell sx={{ color: getSignalColor(item.signal), fontSize: TYPOGRAPHY.tiny, fontWeight: 500 }}>
                      {item.signal}
                    </TableCell>
                    {[item.avg_return_1d, item.avg_return_3d, item.avg_return_7d].map((v, idx) => {
                      const { text, color } = formatPercentWithColor(v);
                      // map -10%..+10% into 0-100 width band (clamped)
                      const width =
                        v === null || Number.isNaN(v)
                          ? 0
                          : Math.max(5, Math.min(100, (Math.abs(v) / 10) * 100));
                      return (
                        <TableCell key={idx} align="right" sx={{ fontSize: TYPOGRAPHY.tiny }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1 }}>
                            <Box sx={{ width: 40, height: 4, bgcolor: 'grey.800', borderRadius: 100, overflow: 'hidden' }}>
                              {v !== null && !Number.isNaN(v) && (
                                <Box
                                  sx={{
                                    width: `${width}%`,
                                    height: '100%',
                                    bgcolor: v >= 0 ? 'success.main' : 'error.main',
                                  }}
                                />
                              )}
                            </Box>
                            <Typography variant="caption" sx={{ fontSize: TYPOGRAPHY.tiny, color }}>
                              {text}
                            </Typography>
                          </Box>
                        </TableCell>
                      );
                    })}
                    <TableCell align="right" sx={{ color: 'text.secondary', fontSize: TYPOGRAPHY.tiny }}>
                      {item.sample_size}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          </Box>
        ) : (
          <Typography variant="caption" sx={{ fontSize: TYPOGRAPHY.tiny, color: 'text.secondary' }}>
            Return analytics will populate once completed horizons exist for each signal type.
          </Typography>
        )}
      </CardContent>
    </Card>
  );
}
